import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Home, MapPinned, RefreshCw, Route, UserPlus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  patient_name: '',
  patient_phone: '',
  patient_address: '',
  visit_date: new Date().toISOString().split('T')[0],
  visit_time: '07:30',
  assigned_to_id: '',
  assigned_to_name: '',
  tests: 'CBC, Thyroid Profile',
  notes: '',
  expected_lat: '',
  expected_lng: '',
};

const HomeCollectionModule = ({ user }) => {
  const [items, setItems] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [checkingInId, setCheckingInId] = useState(null);

  const canAssign = ['admin', 'receptionist'].includes(user.role);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [axios.get(`${API}/staff/home-collections`, { withCredentials: true })];
      if (canAssign) {
        requests.push(axios.get(`${API}/staff/home-collections/assignees`, { withCredentials: true }));
      }
      const responses = await Promise.all(requests);
      setItems(responses[0].data);
      if (canAssign) setAssignees(responses[1]?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load home collections');
    }
    setLoading(false);
  }, [canAssign]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAssign = async (e) => {
    e.preventDefault();
    try {
      const selected = assignees.find((staff) => staff.id === form.assigned_to_id);
      if (!selected) {
        toast.error('Select a staff member');
        return;
      }
      await axios.post(`${API}/staff/home-collections`, {
        ...form,
        assigned_to_name: selected.name,
        tests: form.tests.split(',').map((item) => item.trim()).filter(Boolean),
        expected_lat: Number(form.expected_lat),
        expected_lng: Number(form.expected_lng),
      }, { withCredentials: true });
      toast.success('Home collection assigned');
      setForm(emptyForm);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Assignment failed');
    }
  };

  const updateVisit = async (collectionId, endpoint, payload = {}) => {
    try {
      await axios.put(`${API}/staff/home-collections/${collectionId}/${endpoint}`, payload, { withCredentials: true });
      toast.success('Visit updated');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const getGpsStamp = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported on this device'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const gpsLat = Number(position.coords.latitude.toFixed(6));
        const gpsLng = Number(position.coords.longitude.toFixed(6));
        resolve({
          gps_lat: gpsLat,
          gps_lng: gpsLng,
          gps_map_link: `https://www.google.com/maps?q=${gpsLat},${gpsLng}`,
        });
      },
      () => reject(new Error('Location permission is required for GPS check-in')),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  });

  const handleGpsAction = async (item, endpoint, extraPayload = {}) => {
    setCheckingInId(item.id + endpoint);
    try {
      const gpsStamp = await getGpsStamp();
      await updateVisit(item.id, endpoint, { notes: item.notes || '', ...extraPayload, ...gpsStamp });
    } catch (err) {
      toast.error(err.message || 'GPS capture failed');
    }
    setCheckingInId(null);
  };

  const visibleItems = useMemo(() => items, [items]);

  return (
    <div className="space-y-5" data-testid="home-collection-module">
      <div className="rounded-[1.75rem] bg-white p-6 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-sky-600">Field visits</p>
        <h2 className="mt-2 text-3xl font-semibold text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>Home Collection</h2>
        <p className="mt-2 text-sm text-slate-500">Admin or receptionist can assign a visit. Assigned staff checks in at the patient home, updates tests, and checks out.</p>
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
        <div className="rounded-[1.75rem] bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[#0A2540]">Assigned visits</h3>
            <Button onClick={fetchData} variant="outline" size="icon" data-testid="home-collection-refresh"><RefreshCw className="h-4 w-4" /></Button>
          </div>

          <div className="space-y-3">
            {loading ? (
              <div className="py-8 text-center text-slate-400">Loading home collections...</div>
            ) : visibleItems.length === 0 ? (
              <div className="py-8 text-center text-slate-400">No home collections assigned yet.</div>
            ) : visibleItems.map((item) => (
              <div key={item.id} className="rounded-2xl border border-slate-200 p-4" data-testid={`home-collection-card-${item.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="text-base font-semibold text-[#0A2540]">{item.patient_name}</p>
                    <p className="mt-1 text-sm text-slate-500">{item.patient_phone} • {item.visit_date} at {item.visit_time}</p>
                    <p className="mt-2 text-sm text-slate-600">{item.patient_address}</p>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-xs font-medium ${item.status === 'completed' ? 'bg-green-100 text-green-700' : item.status === 'checked_in' ? 'bg-sky-100 text-sky-700' : item.status === 'in_progress' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{item.status.replace('_', ' ')}</span>
                </div>

                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
                    <p className="font-semibold text-[#0A2540]">Assigned to</p>
                    <p className="mt-1">{item.assigned_to_name}</p>
                    <p className="mt-2 text-xs text-slate-500">Assigned by {item.assigned_by}</p>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
                    <p className="font-semibold text-[#0A2540]">Tests</p>
                    <p className="mt-1">{(item.tests || []).join(', ')}</p>
                    {item.tests_completed?.length > 0 && <p className="mt-2 text-xs text-green-700">Completed: {item.tests_completed.join(', ')}</p>}
                  </div>
                </div>

                <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700" data-testid={`home-collection-expected-location-${item.id}`}>
                  <p className="font-semibold text-[#0A2540]">Expected patient location</p>
                  <p className="mt-2">Lat: {item.expected_lat} | Lng: {item.expected_lng}</p>
                  <a href={item.expected_map_link} target="_blank" rel="noreferrer" className="mt-1 inline-flex text-sky-700 hover:underline">Open expected location</a>
                </div>

                {item.gps_check_in && (
                  <div className="mt-3 rounded-xl border border-sky-100 bg-sky-50 p-3 text-sm text-slate-700" data-testid={`home-collection-gps-${item.id}`}>
                    <div className="flex items-center gap-2 font-semibold text-[#0A2540]"><MapPinned className="h-4 w-4 text-sky-600" /> GPS check-in stamp</div>
                    <p className="mt-2">Lat: {item.gps_check_in.lat} | Lng: {item.gps_check_in.lng}</p>
                    <p className={item.gps_check_in.within_allowed_radius ? 'mt-1 text-green-700' : 'mt-1 text-amber-700'}>Distance from patient: {item.gps_check_in.distance_meters} m</p>
                    <a href={item.gps_check_in.map_link} target="_blank" rel="noreferrer" className="mt-1 inline-flex text-sky-700 hover:underline" data-testid={`home-collection-gps-link-${item.id}`}>Open in Google Maps</a>
                  </div>
                )}

                {item.gps_check_out && (
                  <div className="mt-3 rounded-xl border border-emerald-100 bg-emerald-50 p-3 text-sm text-slate-700" data-testid={`home-collection-gps-out-${item.id}`}>
                    <div className="flex items-center gap-2 font-semibold text-[#0A2540]"><MapPinned className="h-4 w-4 text-emerald-600" /> GPS check-out stamp</div>
                    <p className="mt-2">Lat: {item.gps_check_out.lat} | Lng: {item.gps_check_out.lng}</p>
                    <p className={item.gps_check_out.within_allowed_radius ? 'mt-1 text-green-700' : 'mt-1 text-amber-700'}>Distance from patient: {item.gps_check_out.distance_meters} m</p>
                    <a href={item.gps_check_out.map_link} target="_blank" rel="noreferrer" className="mt-1 inline-flex text-emerald-700 hover:underline" data-testid={`home-collection-gps-out-link-${item.id}`}>Open in Google Maps</a>
                  </div>
                )}

                <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                  {item.status === 'assigned' && <Button onClick={() => handleGpsAction(item, 'checkin')} className="rounded-xl bg-[#0A2540]" disabled={checkingInId === `${item.id}checkin`} data-testid={`home-collection-checkin-${item.id}`}><Route className="mr-2 h-4 w-4" /> {checkingInId === `${item.id}checkin` ? 'Capturing GPS...' : 'Check In with GPS'}</Button>}
                  {item.status !== 'completed' && <Button onClick={() => updateVisit(item.id, 'update-tests', { status: 'in_progress', tests_completed: item.tests || [], notes: item.notes || '' })} variant="outline" className="rounded-xl" data-testid={`home-collection-update-tests-${item.id}`}>Update Tests</Button>}
                  {item.status !== 'completed' && <Button onClick={() => handleGpsAction(item, 'checkout', { visit_summary: 'Samples collected and visit closed', tests_completed: item.tests || [] })} variant="outline" className="rounded-xl border-green-200 text-green-700 hover:bg-green-50" disabled={checkingInId === `${item.id}checkout`} data-testid={`home-collection-checkout-${item.id}`}>{checkingInId === `${item.id}checkout` ? 'Capturing GPS...' : 'Check Out with GPS'}</Button>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          {canAssign && (
            <div className="rounded-[1.75rem] bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#0A2540]/6"><UserPlus className="h-5 w-5 text-[#0A2540]" /></div>
                <div>
                  <h3 className="text-lg font-semibold text-[#0A2540]">Assign new visit</h3>
                  <p className="text-sm text-slate-500">Only admin or receptionist can assign home collection staff.</p>
                </div>
              </div>

              <form onSubmit={handleAssign} className="space-y-4" data-testid="home-collection-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><Label>Patient Name</Label><Input value={form.patient_name} onChange={(e) => setForm((prev) => ({ ...prev, patient_name: e.target.value }))} className="mt-2 h-11 rounded-xl" data-testid="home-collection-patient-name" /></div>
                  <div><Label>Patient Phone</Label><Input value={form.patient_phone} onChange={(e) => setForm((prev) => ({ ...prev, patient_phone: e.target.value }))} className="mt-2 h-11 rounded-xl" data-testid="home-collection-patient-phone" /></div>
                </div>
                <div><Label>Patient Address</Label><Input value={form.patient_address} onChange={(e) => setForm((prev) => ({ ...prev, patient_address: e.target.value }))} className="mt-2 h-11 rounded-xl" data-testid="home-collection-patient-address" /></div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><Label>Expected Latitude</Label><Input type="number" step="0.000001" value={form.expected_lat} onChange={(e) => setForm((prev) => ({ ...prev, expected_lat: e.target.value }))} className="mt-2 h-11 rounded-xl" data-testid="home-collection-expected-lat" required /></div>
                  <div><Label>Expected Longitude</Label><Input type="number" step="0.000001" value={form.expected_lng} onChange={(e) => setForm((prev) => ({ ...prev, expected_lng: e.target.value }))} className="mt-2 h-11 rounded-xl" data-testid="home-collection-expected-lng" required /></div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><Label>Visit Date</Label><Input type="date" value={form.visit_date} onChange={(e) => setForm((prev) => ({ ...prev, visit_date: e.target.value }))} className="mt-2 h-11 rounded-xl" data-testid="home-collection-visit-date" /></div>
                  <div><Label>Visit Time</Label><Input type="time" value={form.visit_time} onChange={(e) => setForm((prev) => ({ ...prev, visit_time: e.target.value }))} className="mt-2 h-11 rounded-xl" data-testid="home-collection-visit-time" /></div>
                </div>
                <div>
                  <Label>Assign Staff</Label>
                  <select className="mt-2 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm" value={form.assigned_to_id} onChange={(e) => setForm((prev) => ({ ...prev, assigned_to_id: e.target.value }))} data-testid="home-collection-assigned-to">
                    <option value="">Select staff</option>
                    {assignees.map((staff) => <option key={staff.id} value={staff.id}>{staff.name} ({staff.role})</option>)}
                  </select>
                </div>
                <div><Label>Tests</Label><Input value={form.tests} onChange={(e) => setForm((prev) => ({ ...prev, tests: e.target.value }))} placeholder="Comma separated test names" className="mt-2 h-11 rounded-xl" data-testid="home-collection-tests" /></div>
                <div>
                  <Label>Notes</Label>
                  <textarea className="mt-2 min-h-[96px] w-full rounded-2xl border border-slate-200 px-3 py-3 text-sm" value={form.notes} onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))} placeholder="Special handling or address notes" data-testid="home-collection-notes" />
                </div>
                <Button type="submit" className="rounded-xl bg-[#0A2540]" data-testid="home-collection-submit"><Home className="mr-2 h-4 w-4" /> Assign Visit</Button>
              </form>
            </div>
          )}

          <div className="rounded-[1.75rem] bg-[#0A2540] p-5 text-white shadow-sm" data-testid="home-collection-guide">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-sky-300">Workflow</p>
            <ul className="mt-4 space-y-3 text-sm text-slate-200">
              <li>• Admin or receptionist assigns a staff member with Home Collection permission.</li>
              <li>• Staff check-in and check-out both capture live GPS coordinates and compare them to the patient location.</li>
              <li>• Distance mismatch is allowed but clearly visible for admin follow-up.</li>
              <li>• Visit status stays visible for operations follow-up.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeCollectionModule;